import { AbstractMesh, GPUParticleSystem, ParticleSystem, Vector3 } from '@babylonjs/core';
import { AbstractMeshBehavior } from './AbstractMeshBehavior';

export class CollectibleAttractionBehavior extends AbstractMeshBehavior {
  private static readonly ATTRACTION_DISTANCE_SQ = 4.0 * 4.0; // squared — avoids sqrt per frame
  private static readonly ATTRACTION_SPEED_BASE = 3.5;
  private static readonly ATTRACTION_ACCELERATION = 10.0;

  private isAttracted: boolean = false;
  private currentSpeed: number = 0;
  private particleSystem: ParticleSystem | GPUParticleSystem | null = null;

  // Pre-allocated vectors — zero heap allocs in beforeRender
  private readonly targetPos: Vector3 = new Vector3();
  private readonly direction: Vector3 = new Vector3();
  private readonly step: Vector3 = new Vector3();
  private static readonly playerOffset: Vector3 = new Vector3(0, 1.0, 0);

  constructor() {
    super('collectibleAttraction');
  }

  attach(mesh: AbstractMesh): void {
    super.attach(mesh);
    this.currentSpeed = CollectibleAttractionBehavior.ATTRACTION_SPEED_BASE;
  }

  detach(): void {
    super.detach();
    //this.disposeParticles();
  }

  disposeParticles(): void {
    if (this.particleSystem) {
      this.particleSystem.stop();
      this.particleSystem.dispose();
      this.particleSystem = null;
    }
  }

  beforeRender(): void {
    if (!this.mesh || !this.level || !this.level.player) return;

    const player = this.level.player;
    if (!player.alive || player.isDying) return;

    // Zero-alloc target position
    player.model.position.addToRef(CollectibleAttractionBehavior.playerOffset, this.targetPos);

    if (!this.isAttracted) {
      // Cheap early-exit path — only a squared distance check, no movement math
      if (Vector3.DistanceSquared(this.mesh.position, this.targetPos) < CollectibleAttractionBehavior.ATTRACTION_DISTANCE_SQ) {
        this.isAttracted = true;
        this.currentSpeed = CollectibleAttractionBehavior.ATTRACTION_SPEED_BASE;
      } else {
        return; // Not close enough — bail immediately (cheap for the vast majority of frames)
      }
    }

    // TODO: too expensive
    // if (!this.particleSystem) {
    //   this.particleSystem = this.level.particles.createStarGlitterParticleSystem(this.mesh.position, this.level);
    // }

    // Attracted path — runs only when collectible is flying toward player
    const deltaTime = this.level.game.scene.getEngine().getDeltaTime() / 1000;
    this.currentSpeed += CollectibleAttractionBehavior.ATTRACTION_ACCELERATION * deltaTime;

    // Zero-alloc movement: subtract → normalize → scale → addInPlace
    this.targetPos.subtractToRef(this.mesh.position, this.direction);
    this.direction.normalizeToRef(this.direction);
    this.direction.scaleToRef(this.currentSpeed * deltaTime, this.step);
    this.mesh.position.addInPlace(this.step);

    // Force collection when very close (avoids hover/flicker)
    const distSq = Vector3.DistanceSquared(this.mesh.position, this.targetPos);
    if (distSq < 0.25) {
      if (this.mesh.metadata?.playerCollision && typeof this.mesh.metadata.playerCollision.action === 'function') {
        this.mesh.metadata.playerCollision.action(this.level, player);
        this.isAttracted = false;
        //this.disposeParticles();
      }
    }
  }

  clone(): CollectibleAttractionBehavior {
    return new CollectibleAttractionBehavior();
  }
}
